import { Capacitor } from '@capacitor/core';

let initialized = false;

/**
 * Lazily initialize Firebase Analytics and log a single `app_open` event.
 *
 * Fire-and-forget by design: called from a `useEffect` and never awaited, so a
 * blocked `googletagmanager.com` (or missing Firebase config) drops analytics
 * data but never blocks the first paint.  Device / country / session length /
 * active users are collected automatically by Firebase — we only emit the one
 * `app_open` marker here.
 *
 * Native path logs the same `app_open` through `@capacitor-firebase/analytics`
 * (installed + `cap sync`-ed), which routes to the Firebase iOS/Android SDK
 * inside the Capacitor WebView.
 */
export async function initAnalytics() {
  if (initialized) return;
  try {
    if (Capacitor.isNativePlatform()) {
      const { FirebaseAnalytics } = await import(/* @vite-ignore */ '@capacitor-firebase/analytics');
      await FirebaseAnalytics.setEnabled({ enabled: true });
      await FirebaseAnalytics.logEvent({ name: 'app_open' });
      initialized = true;
      return;
    }
    const [{ default: app }, { getAnalytics, logEvent }] = await Promise.all([
      import('./firebase'),
      import('firebase/analytics'),
    ]);
    const analytics = getAnalytics(app);
    logEvent(analytics, 'app_open');
    initialized = true;
  } catch (e) {
    // Missing VITE_FIREBASE_MEASUREMENT_ID, blocked network, etc. — swallow.
    console.warn('Analytics init skipped:', e);
  }
}
